import type { DmNotification } from '../../../../services/dmNotifications';
import { levelRank } from '../dmNotificationLevels.ts';

export type NotificationCardSortKey = 'date' | 'level';
export type NotificationCardSortOrder = 'asc' | 'desc';

export interface NotificationCardSort {
    key: NotificationCardSortKey;
    order: NotificationCardSortOrder;
}

// Newest first, the order the server sends.
export const DEFAULT_CARD_SORT: NotificationCardSort = { key: 'date', order: 'desc' };

export function isNotificationCardSort(value: unknown): value is NotificationCardSort {
    if (typeof value !== 'object' || value === null) return false;
    const { key, order } = value as Partial<NotificationCardSort>;
    return (key === 'date' || key === 'level') && (order === 'asc' || order === 'desc');
}

function createdTime(notification: DmNotification): number {
    const time = Date.parse(notification.createdAt);
    return Number.isNaN(time) ? 0 : time;
}

function newestFirst(a: DmNotification, b: DmNotification): number {
    return createdTime(b) - createdTime(a) || a.id.localeCompare(b.id);
}

// Desc = most urgent first; within a level it's always newest first, whichever way the levels run.
function byLevel(order: NotificationCardSortOrder) {
    const direction = order === 'desc' ? 1 : -1;
    return (a: DmNotification, b: DmNotification) =>
        direction * (levelRank(a.level) - levelRank(b.level)) || newestFirst(a, b);
}

function byDate(order: NotificationCardSortOrder) {
    return (a: DmNotification, b: DmNotification) =>
        order === 'desc' ? newestFirst(a, b) : newestFirst(b, a);
}

export function sortNotificationCards<T extends DmNotification>(
    notifications: readonly T[],
    sort: NotificationCardSort
): T[] {
    const compare = sort.key === 'level' ? byLevel(sort.order) : byDate(sort.order);
    return [...notifications].sort(compare);
}

const DIRECTION_LABELS: Record<
    NotificationCardSortKey,
    Record<NotificationCardSortOrder, string>
> = {
    date: { desc: 'newest first', asc: 'oldest first' },
    level: { desc: 'most urgent first', asc: 'least urgent first' }
};

export function sortDirectionLabel(
    key: NotificationCardSortKey,
    order: NotificationCardSortOrder
): string {
    return DIRECTION_LABELS[key][order];
}
